// @ts-nocheck
import styled, { css } from 'styled-components';
import type {
  TypographyProps,
  SpaceProps,
  ColorProps,
  LayoutProps,
  FlexboxProps
} from 'styled-system';
import { variant, color, space, layout, flexbox, typography } from 'styled-system';
import Text from '@app/ui/Text/Text';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import type { TextStyles } from '@ingka/typography-shared';

// Mapping from Kompis ThemeFontStyleTypeEnum to Skapa textStyle
// Skapa sizes: Heading.XS=14px, Heading.S=16px, Heading.M=18px, Heading.L=24px, Heading.XL=32px
const textStyleMap: Record<string, TextStyles> = {
  [ThemeFontStyleTypeEnum.headingXSmall]: 'Heading.XS',
  [ThemeFontStyleTypeEnum.headingSmall]: 'Heading.S',
  [ThemeFontStyleTypeEnum.headingMedium]: 'Heading.M',
  [ThemeFontStyleTypeEnum.headingLarge]: 'Heading.L',
  [ThemeFontStyleTypeEnum.headingXLarge]: 'Heading.XL'
};

// @see https://fundament.ikea.com/foundation/typography#typeStyles
type Type =
  | ThemeFontStyleTypeEnum.headingXSmall
  | ThemeFontStyleTypeEnum.headingSmall
  | ThemeFontStyleTypeEnum.headingMedium
  | ThemeFontStyleTypeEnum.headingLarge
  | ThemeFontStyleTypeEnum.headingXLarge;

export enum HeadingType {
  HeadingOne = 'h1',
  HeadingTwo = 'h2',
  HeadingThree = 'h3',
  HeadingFour = 'h4'
}

const headingSharedCss = css`
  word-break: normal;
  overflow-wrap: anywhere;
  margin: 0;
  ${color};
  ${space};
  ${typography};
  ${layout}
  ${flexbox}
  ${variant({
    variants: {
      title60: {
        lineHeight: 'narrow'
      },
      title48: {
        lineHeight: 'narrow'
      },
      title38: {
        lineHeight: 'shorter'
      },
      title30: {
        lineHeight: 'short'
      },
      title24: {
        lineHeight: 'short'
      }
    }
  })}
`;

const Heading = styled.h2`
  ${headingSharedCss}
`;

type AsProps = {
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
  forwardedAs?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
};

type ExtendProps = React.PropsWithoutRef<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>
> &
  SpaceProps &
  ColorProps &
  TypographyProps &
  LayoutProps &
  FlexboxProps &
  AsProps;

export interface TitleProps extends ExtendProps {
  /** Type of title as described in Fundament */
  type: Type;
  /** React element, text, or fragment */
  heading?: HeadingType;
  children: React.ReactNode;
  color?: string;
}

const Title = ({
  type,
  heading = HeadingType.HeadingTwo,
  children,
  color = 'text',
  ...props
}: TitleProps) => {
  // Don't render empty heading - accessibility violation
  if (!children || (typeof children === 'string' && children.trim() === '')) {
    return null;
  }

  const textStyle = textStyleMap[type] || 'Heading.M';

  return (
    <Heading as={heading} variant={type} {...props} color={color}>
      <Text textStyle={textStyle} style={props.style}>
        {children}
      </Text>
    </Heading>
  );
};

export default Title;
